<script setup lang="ts">
import {PageHeading} from "@/Components/Global";
import {Button, Card, ColorPicker, InputText, Label, Textarea} from "@/Components/Library";
import {useStorage} from "@vueuse/core";
import {inviteUser} from "@/Services/Admin/admin-users";
import {AnonymizationSelect, ResetButton} from "@/Components/Forms";
import {storeGroup} from "@/Services/Admin/admin-groups";
import {inviteContacts} from "@/Services/Admin/admin-contacts";
import {ref} from "vue";
import {useToast} from "primevue/usetoast";
import {useRouter} from "vue-router";

const router = useRouter();
const toast = useToast();

const defaultUserForm = {
    id: null,
    first_name: null,
    last_name: null,
    email: null,
};

const defaultGroupForm = {
    id: null,
    name: null,
    description: null,
    anonymization: null,
    color: null,
    tags: [],
};

const user = useStorage("client-user-form", {...defaultUserForm});
const group = useStorage("client-group-form", {...defaultGroupForm});
const invitees = useStorage("client-invitees-form", [defaultUserForm]);

const saving = ref<string | boolean>(false);

const resetForm = () => {
    user.value = {...defaultUserForm};
    group.value = {...defaultGroupForm};
    invitees.value = [defaultUserForm];
};

const save = async () => {
    try {
        if (!user.value.id) {
            // User
            saving.value = "user";
            const {data} = await inviteUser(user.value);
            Object.assign(user.value, data);
        }

        if (!group.value.id) {
            // Group
            saving.value = "group";
            let postGroupData = {
                id: group.value.id,
                name: group.value.name,
                description: group.value.description,
                anonymization: group.value.anonymization,
                color: group.value.color,
                user_id: user.value.id,
                tags: group.value.tags.map((tag) => tag.name),
            };
            const {data} = await storeGroup(postGroupData);
            Object.assign(group.value, data);
        }

        // Contacts
        saving.value = "contacts";
        let postInviteesData = invitees.value.map((invitee) => ({
            first_name: invitee.first_name,
            last_name: invitee.last_name,
            email: invitee.email,
        }));
        console.log("group id", group.value.id);
        const {data} = await inviteContacts(
            {
                user_id: user.value.id,
                contacts: postInviteesData,
            },
            group.value.id,
        );

        Object.assign(invitees.value, data);

        toast.add({
            severity: "success",
            summary: "Success",
            detail: "User, group, and contacts created successfully",
            life: 3000,
        });
        setTimeout(() => {
            resetForm();
            router.push({name: "admin.users.index"});
        }, 3000);
    } catch (e) {
        console.error(e);
        toast.add({
            severity: "error",
            summary: "Error saving " + saving.value,
            detail: e.response.data.message,
            life: 3000,
        });
    } finally {
        saving.value = false;
    }
};

const addInvitee = () =>
    invitees.value.push({
        ...defaultUserForm,
    });
</script>
<template>
    <div class="mx-auto w-full max-w-5xl">
        <div class="m-container mb-3">
            <PageHeading title="Create Client">
                <ResetButton @reset="resetForm" />
            </PageHeading>
        </div>
        <form class="flex flex-col gap-4" @submit.prevent="save">
            <Card>
                <template #title>User</template>
                <template v-if="user.id">
                    <table>
                        <tbody>
                            <tr>
                                <th>ID</th>
                                <td>{{ user.id }}</td>
                            </tr>
                            <tr>
                                <th>First name</th>
                                <td>{{ user.first_name }}</td>
                            </tr>
                            <tr>
                                <th>Last name</th>
                                <td>{{ user.last_name }}</td>
                            </tr>
                            <tr>
                                <th>Email</th>
                                <td>{{ user.email }}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>
                <template v-else>
                    <div class="flex flex-col gap-4">
                        <div class="grid-cols-2 gap-6 md:grid">
                            <div class="">
                                <Label for="first_name" class="mb-1" required>First name</Label>
                                <InputText
                                    id="first_name"
                                    name="first_name"
                                    placeholder="First name"
                                    v-model="user.first_name"
                                    required
                                    fluid
                                />
                            </div>
                            <div>
                                <Label for="last_name" class="mb-1" required>Last name</Label>
                                <InputText
                                    id="last_name"
                                    name="last_name"
                                    placeholder="Last name"
                                    v-model="user.last_name"
                                    required
                                    fluid
                                />
                            </div>
                        </div>
                        <div>
                            <Label for="email" class="mb-1" required>Email</Label>
                            <InputText
                                id="email"
                                name="email"
                                placeholder="Email address"
                                v-model="user.email"
                                required
                                fluid
                            />
                        </div>
                    </div>
                </template>
            </Card>
            <Card>
                <template #title>Group</template>
                <template v-if="group.id">
                    <table>
                        <tbody>
                            <tr>
                                <th>ID</th>
                                <td>{{ group.id }}</td>
                            </tr>
                            <tr>
                                <th>Name</th>
                                <td>{{ group.name }}</td>
                            </tr>
                            <tr>
                                <th>Description</th>
                                <td>{{ group.description ?? "-" }}</td>
                            </tr>
                            <tr>
                                <th>Anonymization</th>
                                <td>{{ group.anonymization.name }}</td>
                            </tr>
                        </tbody>
                    </table>
                </template>
                <template v-else>
                    <div class="flex flex-col gap-4">
                        <div class="flex flex-col">
                            <Label for="name" class="mb-1" required>Name</Label>
                            <InputText id="name" name="name" v-model="group.name" required fluid />
                        </div>
                        <div class="flex flex-col">
                            <Label for="description" class="mb-1">Description</Label>
                            <Textarea
                                id="description"
                                name="description"
                                v-model="group.description"
                                fluid
                                auto-resize
                            />
                        </div>
                        <div class="mb--6">
                            <h3 class="mb-2 font-bold">Anonymization</h3>
                            <AnonymizationSelect v-model="group.anonymization" />
                        </div>
                        <div class="flex flex-col">
                            <h3 class="mb-2 font-bold">Color</h3>
                            <ColorPicker v-model="group.color" />
                        </div>
                    </div>
                </template>
            </Card>
            <Card>
                <template #title>Group members</template>
                <template v-if="invitees[0].id">
                    <div class="flex flex-col gap-4">
                        <template v-for="(invitee, i) in invitees" :key="i">
                            <div class="flex items-center gap-4">
                                <div
                                    class="flex-0 flex h-8 w-8 items-center justify-center rounded-full bg-askr-green font-bold text-white"
                                >
                                    {{ i + 1 }}
                                </div>
                                <div>
                                    <span>{{ invitee.first_name }}</span>
                                    {{ " " }}
                                    <span class="font-bold">{{ invitee.last_name }}</span>
                                </div>
                                <span>{{ invitee.email }}</span>
                            </div>
                        </template>
                    </div>
                </template>
                <template v-else>
                    <div class="flex flex-col gap-4" v-auto-animate>
                        <template v-for="(invitee, i) in invitees" :key="i">
                            <div class="flex items-center gap-4">
                                <div
                                    class="flex-0 flex h-8 w-8 items-center justify-center rounded-full bg-askr-green font-bold text-white"
                                >
                                    {{ i + 1 }}
                                </div>
                                <div class="flex grow flex-col">
                                    <Label :for="`first_name${i}`" class="mb-1" required>Member's first name</Label>
                                    <InputText
                                        :id="`first_name${i}`"
                                        :name="`first_name${i}`"
                                        v-model="invitees[i].first_name"
                                        required
                                        fluid
                                    />
                                </div>
                                <div class="flex grow flex-col">
                                    <Label :for="`last_name${i}`" class="mb-1" required>Member's last name</Label>
                                    <InputText
                                        :id="`last_name${i}`"
                                        :name="`last_name${i}`"
                                        v-model="invitees[i].last_name"
                                        required
                                        fluid
                                    />
                                </div>
                                <div class="flex grow flex-col">
                                    <Label :for="`email${i}`" class="mb-1" required>Member's email</Label>
                                    <InputText
                                        :id="`email${i}`"
                                        :name="`email${i}`"
                                        type="email"
                                        v-model="invitees[i].email"
                                        required
                                        fluid
                                    />
                                </div>
                                <Button
                                    :disabled="invitees.length === 1"
                                    :icon="{icon: 'mdi:trash-outline', width: '20'}"
                                    class="ml-auto h-8 w-8 bg-red-600 text-white"
                                    @click="invitees.splice(i, 1)"
                                />
                            </div>
                        </template>
                        <Button
                            @click="addInvitee"
                            class="self-center bg-askr-green !p-2 text-white hover:brightness-110"
                            :icon="{icon: 'mdi:plus', width: '28'}"
                        />
                    </div>
                </template>
            </Card>
            <div class="m-container">
                <Button
                    :disabled="user.id && group.id && invitees[0].id"
                    :loading="!!saving"
                    type="submit"
                    class="w-full bg-askr-green text-white"
                    icon="fluent:checkmark-12-regular"
                >
                    {{ saving ? "Saving " + saving : "Save" }}
                </Button>
            </div>
        </form>
    </div>
</template>
<style scoped>
th {
    @apply whitespace-nowrap pr-2 text-left font-bold;
}

td {
    @apply w-full;
}
</style>
